import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { TrajectoryChart } from './Charts.jsx'
import { TeamMark } from './Marks.jsx'
import SeasonHead, { ViewFrame } from './SeasonHead.jsx'
import { seriesColor, teamStyle, toneStyle } from './colors.js'
import { validTeamId } from './history.js'
import { useHead, useHistoryResource, useSeasonChoice, useTeams } from './historyStore.jsx'
import { ROSTER_WIDE_PX, useMinWidth } from './layout.js'
import { listPath, playerPath } from './paths.js'
import { LOADING, READY } from './poll.js'
import { standingsRow, teamModel, trajectoryModel } from './teamModel.js'
import { backState, useBack } from './useBack.js'
import { useDocumentTitle } from '../../useDocumentTitle.js'

// A team's page. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// THE TEAM'S OWN FILE IS THE PAGE; THE STANDINGS ARE WHERE ITS RANK COMES FROM
// ═══════════════════════════════════════════════════════════════════════════
//
// `teamModel.js` draws a season line (points, rebounds, assists and the rest), the ratings with the pace, home and road
// from the game log, the trajectory as games over .500 (with its best mark and when it came) and the roster by points.
// The standings row contributes the seed (`1st in the West · 1st Northwest`), the streak, the last ten, and the
// conference and division records. Every team has a file in every published season, so there is no page built from a
// standings row alone: a team with no file is a page that is not there (`teamModel` is `null` for it).
//
// THE ROSTER GROWS WITH THE VIEWPORT, in columns, not in rows: GP, PTS, REB and AST on a phone, with MIN and FG% from
// 560px (`layout.js`). It is one table with the extra cells drawn only when there is room (the same row twice, or hidden
// cells in the document, would put each number in the document twice). A row opens the player's page, with the team's
// name as the Back label.
//
// A season the bot has not got a file for this team in is a page with only its head; the season control is the way on.

function BigStat({ cell }) {
  return (
    <div className="d2d-hoops__stat" data-large="true" data-testid={`hoops-team-stat-${cell.key}`}>
      <div className="d2d-hoops__statvalue">{cell.value}</div>
      <div className="d2d-hoops__statlabel d2d-micro">{cell.label}</div>
    </div>
  )
}

function SmallStat({ cell }) {
  return (
    <div className="d2d-hoops__stat" data-large="false" data-testid={`hoops-team-stat-${cell.key}`}>
      <div className="d2d-hoops__statvalue">{cell.value}</div>
      <div className="d2d-hoops__statlabel d2d-micro">{cell.label}</div>
    </div>
  )
}

function TeamBody({ teamId }) {
  const choice = useSeasonChoice()
  const teams = useTeams()
  const wide = useMinWidth(ROSTER_WIDE_PX)
  const [picked, setPicked] = useState(null)
  const season = choice.selected?.season ?? null
  const file = useHistoryResource('team', season, teamId)
  const standings = useHistoryResource('standings', season)
  const back = useBack('Teams', listPath('teams', season))
  const waiting = file.state === LOADING || standings.state === LOADING

  const row = standings.state === READY ? standingsRow(standings.data, Number(teamId)) : null
  const model =
    waiting || file.state !== READY
      ? null
      : teamModel({
          file: file.data,
          row,
          standings: standings.state === READY ? standings.data : null,
          wide,
          nameFor: (id, abbr) => teams.nameOf(id, abbr),
          teamId: Number(teamId),
        })
  const head = useHead({ season, entry: choice.selected, asOf: model?.asOf, source: model?.source, ready: model !== null })
  useDocumentTitle(model?.name ?? 'Team')

  if (choice.selected === null) return null

  const team = teams.byId(Number(teamId))
  const chart = model && model.trajectory ? trajectoryModel({ trajectory: model.trajectory, selected: picked }) : null
  const names = model ? teams.namesOf(Number(teamId), model.abbr, model.name) : null

  return (
    <div
      className="d2d-hoops__view"
      data-testid="hoops-team"
      data-stale={head.stale ? 'true' : 'false'}
      data-team-id={teamId}
      style={teamStyle(team)}
    >
      <SeasonHead choice={choice} unavailable={NONE} head={head} title={null} back={back} />
      {model === null ? null : (
        <div className="d2d-hoops__pagecols" data-testid="hoops-team-cols">
          <div className="d2d-hoops__pagecol">
            <section
              className="d2d-hoops__phero"
              aria-label={model.name}
              style={toneStyle(team, null, { faded: head.stale })}
              data-testid="hoops-team-hero"
            >
              <div className="d2d-hoops__pheroid">
                <TeamMark team={team} tricode={model.abbr} which="away" size="xl" />
                <div className="d2d-hoops__pheroname">
                  <h1 className="d2d-hoops__h1" data-testid="hoops-team-name">
                    {names.city === '' ? model.name : `${names.city} ${names.nick}`}
                  </h1>
                  <div className="d2d-hoops__pmeta d2d-micro" data-testid="hoops-team-meta">
                    {model.meta}
                  </div>
                </div>
              </div>
              <div className="d2d-hoops__bigstats" data-testid="hoops-team-big">
                {model.big.map((cell) => (
                  <BigStat key={cell.key} cell={cell} />
                ))}
              </div>
              {model.small.length === 0 ? null : (
                <div className="d2d-hoops__smallstats">
                  {model.small.map((cell) => (
                    <SmallStat key={cell.key} cell={cell} />
                  ))}
                </div>
              )}
              <div className="d2d-hoops__records" data-testid="hoops-team-records">
                {model.records.map((record) => (
                  <div key={record.label}>
                    <div className="d2d-hoops__recordvalue">{record.value}</div>
                    <div className="d2d-hoops__recordlabel d2d-micro">{record.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {model.ratings === null ? null : (
              <section className="d2d-hoops__panel" aria-label="Ratings" data-testid="hoops-team-ratings">
                <div className="d2d-hoops__panelhead">
                  <span className="d2d-hoops__paneltitle d2d-micro">Ratings {'·'} per 100 poss.</span>
                  <span className="d2d-hoops__panelnote d2d-micro">{model.pace}</span>
                </div>
                <div className="d2d-hoops__ratings">
                  {model.ratings.map((rating) => (
                    <div className="d2d-hoops__rating" key={rating.label}>
                      <div className="d2d-hoops__ratinglabel d2d-micro">{rating.label}</div>
                      <div className="d2d-hoops__ratingvalue">{rating.value}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {chart === null ? null : (
              <section className="d2d-hoops__panel" aria-label="Trajectory" data-testid="hoops-team-trajectory">
                <span className="d2d-hoops__paneltitle d2d-micro">Win{'–'}loss trajectory</span>
                <TrajectoryChart model={chart} color={seriesColor(team)} onSelect={setPicked} testId="hoops-team-traj" />
              </section>
            )}
          </div>

          <div className="d2d-hoops__pagecol">
            {model.roster === null ? null : (
              <section
                className="d2d-hoops__panel d2d-hoops__panel--flush"
                aria-label="Roster"
                style={{ '--hoops-rostercols': model.roster.columns }}
                data-testid="hoops-team-roster"
              >
                <div className="d2d-hoops__panelhead">
                  <span className="d2d-hoops__paneltitle d2d-micro">Roster {'·'} per game</span>
                  <span className="d2d-hoops__panelnote d2d-micro">{model.roster.count}</span>
                </div>
                <div className="d2d-hoops__rosterrow d2d-hoops__rosterrow--head d2d-micro" aria-hidden="true">
                  <span>Player</span>
                  {model.roster.heads.map((head2) => (
                    <span className="d2d-hoops__rostercell" key={head2}>
                      {head2}
                    </span>
                  ))}
                </div>
                {model.roster.rows.map((player) => (
                  <Link
                    key={player.playerId}
                    to={playerPath(player.playerId, season)}
                    state={backState(model.name)}
                    className="d2d-hoops__rosterrow"
                    data-testid="hoops-roster-row"
                  >
                    <span className="d2d-hoops__rostername">
                      <span className="d2d-hoops__itemname">{player.name}</span>
                      <span className="d2d-hoops__itemsub d2d-micro">{player.sub}</span>
                    </span>
                    {player.cells.map((cell, index) => (
                      <span className="d2d-hoops__rostercell" key={model.roster.heads[index]}>
                        {cell}
                      </span>
                    ))}
                  </Link>
                ))}
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const NONE = new Set()

export default function TeamView() {
  const { teamId } = useParams()
  const valid = validTeamId(teamId)
  return (
    <ViewFrame tab="teams">
      {valid === null ? (
        <p className="d2d-hoops__none" data-testid="hoops-team-invalid">
          That is not a team id.
        </p>
      ) : (
        // Keyed by the id, so following a link from one team to another starts over (the selected game is local state).
        <TeamBody key={valid} teamId={valid} />
      )}
    </ViewFrame>
  )
}
